<template>
  <div v-if="allBanners.length" class="notifications_banner_container">
    <div style="width: 100%">
      <v-card v-for="banner in allBanners" :key="banner.id" class="notifications_banner_card" variant="flat" :color="banner.type">
        <v-alert class="notifications_banner_alert" :closable="true" @click:close="closeBanner(banner.id)" density="compact" :type="banner.type" :icon="banner.icon">
          <span v-html="banner.text"></span>
          
          <template v-if="banner.links && banner.links.length">
            <a :href="link.href" v-for="link in banner.links" :key="link.href" v-html="link.href_text"></a>
          </template>
          
          <div class="notifications_banner_buttons" v-if="banner.buttons && banner.buttons.length">
            <v-btn v-for="button in banner.buttons" :key="button.button_text" :class="button.button_class + ' tech_primary_button mt-3'">
              {{ button.button_text }}
            </v-btn>
          </div>
        </v-alert>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps } from 'vue';
import { useUserDataStore } from '@/store/userDataStore';

const props = defineProps<{
  banners: Banner[]; // Define the prop type
}>();

interface Banner {
  id: string;
  severity: string;
  text: string;
  buttons?: {
    button_text: string;
    button_class: string;
  }[];
  links?: {
    href: string;
    href_text: string;
  }[];
}

function mapSeverityToBannerProperties(severity: string): { type: any; icon: string } {
  switch (severity) {
    case 'information':
    return { type: 'info', icon: 'mdi-information' };
    case 'success':
    return { type: 'success', icon: 'mdi-check-circle' };
    case 'important':
    return { type: 'warning', icon: 'mdi-alert' };
    case 'warning':
    return { type: 'error', icon: 'mdi-emoticon-confused' };
    default:
    return { type: '', icon: '' };
  }
}

const monthsToReappear = 1;

function isBannerClosed(bannerId: string): boolean {
  const closedDateStr = localStorage.getItem(`bannerClosed-${bannerId}`);
  if (!closedDateStr) return false;
  
  const closedDate = new Date(closedDateStr);
  const reappearDate = new Date();
  reappearDate.setMonth(reappearDate.getMonth() - monthsToReappear);
  return closedDate > reappearDate;
}

function closeBanner(bannerId: string) {
  const currentDate = new Date().toISOString();
  localStorage.setItem(`bannerClosed-${bannerId}`, currentDate);
}

const userDataStore = useUserDataStore();

const allBanners = computed(() => {
  return userDataStore.banners
  .filter((banner: Banner) => !isBannerClosed(banner.id))
  .map((banner: Banner) => {
    const bannerProps = mapSeverityToBannerProperties(banner.severity);
    return {
      ...banner,
      type: bannerProps.type,
      icon: bannerProps.icon,
    };
  });
});
</script>

<style lang="scss">
// Styling for the notifications banner skeleton loader
.notifications_banner_container {
  @include display_flex_center;

  // Styling for the notifications banner card
  .notifications_banner_card {
    @include display_flex_center;
    flex-direction: column;
    border-radius: 0px;
    border-bottom: 1px solid;
    border-color: $primary;
  }

  // Styling for the variable notifications banner alert and font colors
  .v-alert__content {
    text-align: center;
    display: flex;
    justify-content: center;
    flex: 1 1 100%;

    span {
      display: inline-block;
      width: 100%;
    }
    &.bg-info {
      color: $white;
    }
    &.bg-success,
    &.bg-warning {
      color: $site-purple;
    }
    &.bg-error {
      color: $white;
    }
  }
}

// Styling for the notifications banner alert
.notifications_banner_alert {
  max-width: 900px;
  gap: 5px;
  display: flex;
  width: 100%;
  justify-content: space-between;

  // Ensure the button text is white
  .admin_consent_button {
    color: #fff;
  }
  
  // Align the alert prepend icon in the center
  .v-alert__prepend {
    align-self: center;
  }
  
  // Align the alert close icon in the center
  .v-alert__close {
    align-self: center;
  }
  
  .v-icon {
    font-size: 25px !important;
    height: 25px !important;
    width: 25px !important;
  }
  
  .v-btn {
    margin: 0px;
  }
  
  // Center the Notification Banner Buttons inside a div
  .notifications_banner_buttons {
    @include display_flex_center;
  }
  
  .mdi-information {
    color: $white;
    opacity: 1;
  }
  
  .mdi-check-circle {
    color: $primary;
    opacity: 1;
  }
  
  .mdi-alert {
    color: $primary;
    opacity: 1;
  }
  
  .mdi-emoticon-confused {
    color: $white;
    opacity: 1;
  }
  
  .mdi-close::before {
    content: '\F0159';
    color: $white;
  }
}

// Mobile styling for admin consent button
@media screen and (max-width: $mobile-screen) {
  .notifications_banner_alert {
    .admin_consent_button {
      height: 35px;
      min-width: 100px;
      margin-left: 0px;
      font-size: unset;
      color: #fff;
    }
  }
}

</style>
